import type { UseFormRegister, FieldErrors, Path, FieldValues } from 'react-hook-form'
import { FormSection } from './form-section'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface BankFieldsProps<T extends FieldValues> {
  register: UseFormRegister<T>
  errors: FieldErrors<T>
  bankNameName: Path<T>
  bankAgencyName: Path<T>
  bankAccountName: Path<T>
  bankAccountTypeName: Path<T>
  pixKeyName: Path<T>
  disabled?: boolean
}

export function BankFields<T extends FieldValues>({
  register,
  errors,
  bankNameName,
  bankAgencyName,
  bankAccountName,
  bankAccountTypeName,
  pixKeyName,
  disabled,
}: BankFieldsProps<T>) {
  type ErrorValue = { message?: string }

  function err(name: Path<T>) {
    const e = errors[name] as ErrorValue | undefined
    return e?.message
  }

  return (
    <FormSection title="Dados bancários">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={String(bankNameName)}>Banco</Label>
          <Input
            id={String(bankNameName)}
            placeholder="Ex: Itaú, Bradesco…"
            disabled={disabled}
            {...register(bankNameName)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={String(bankAccountTypeName)}>Tipo de conta</Label>
          <select
            id={String(bankAccountTypeName)}
            disabled={disabled}
            {...register(bankAccountTypeName)}
            className={cn(
              'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              err(bankAccountTypeName) && 'border-destructive focus-visible:ring-destructive/50',
            )}
          >
            <option value="">Selecione…</option>
            <option value="corrente">Corrente</option>
            <option value="poupanca">Poupança</option>
          </select>
          {err(bankAccountTypeName) && (
            <p className="text-xs text-destructive">{err(bankAccountTypeName)}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={String(bankAgencyName)}>Agência</Label>
          <Input
            id={String(bankAgencyName)}
            placeholder="0000"
            disabled={disabled}
            {...register(bankAgencyName)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={String(bankAccountName)}>Conta</Label>
          <Input
            id={String(bankAccountName)}
            placeholder="00000-0"
            disabled={disabled}
            {...register(bankAccountName)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={String(pixKeyName)}>Chave Pix</Label>
        <Input
          id={String(pixKeyName)}
          placeholder="CPF, email, telefone ou chave aleatória"
          disabled={disabled}
          {...register(pixKeyName)}
        />
      </div>
    </FormSection>
  )
}
